/* eslint-disable react/jsx-key */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Button, Form, Row, Spinner, Table } from 'react-bootstrap';
import debounce from 'lodash.debounce';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faArrowUp } from '@fortawesome/free-solid-svg-icons';
import { TablePlaceholder } from 'components/tables/TablePlaceholder';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable
} from '@tanstack/react-table';
import { useLocation, useNavigate } from 'react-router-dom';

interface DataTableProps<T> {
  queryKey: any[];
  queryFn?: (
    page: number,
    pageSize: number,
    searchTerm: string,
    orderBy: string,
    orderByDesc: boolean,
    filters: object | undefined
  ) => Promise<any>;
  columns: ColumnDef<T>[];
  rightSideActions?: React.ReactNode;
  tableStyleOverride?: string;
  showActions?: boolean;
  filters?: object;
  overrideData?: any;
  getRowStyle?: (row: T) => React.CSSProperties;
  defaultOrderBy?: string;
  defaultOrderByDesc?: boolean;
  renderEmptyState?: (context: { searchTerm: string }) => React.ReactNode;
  onEmptyStateChange?: (context: {
    isEmpty: boolean;
    searchTerm: string;
  }) => void;
  onRowsChange?: (rows: T[]) => void;
  /** When false, the first fetch waits. Use this until required context such as the branch exists. */
  enabled?: boolean;
}

const pageSize = 10;

const getPageCount = (page: any): number => {
  if (Array.isArray(page)) return 1;

  const pageCountValue =
    page?.pageCount ??
    page?.PageCount ??
    page?.totalPages ??
    page?.TotalPages ??
    1;

  return typeof pageCountValue === 'number' && pageCountValue > 0
    ? pageCountValue
    : 1;
};

const getCurrentPage = (page: any, pagesLength: number): number => {
  if (Array.isArray(page)) return pagesLength;

  const current =
    page?.currentPage ?? page?.CurrentPage ?? pagesLength;

  return typeof current === 'number' && current > 0 ? current : pagesLength;
};

const extractRows = (page: any): any[] => {
  if (Array.isArray(page)) return page;
  const rows = page?.data ?? page?.Data ?? [];
  return Array.isArray(rows) ? rows : [];
};

const DataTable = <T,>({
  queryKey,
  queryFn,
  columns,
  rightSideActions,
  tableStyleOverride,
  showActions = true,
  filters,
  overrideData,
  getRowStyle,
  defaultOrderBy,
  defaultOrderByDesc = false,
  renderEmptyState,
  onEmptyStateChange,
  onRowsChange,
  enabled = true
}: React.PropsWithChildren<DataTableProps<T>>) => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const loadMoreRef = React.useRef<HTMLDivElement | null>(null);
  const hasNextPageRef = React.useRef(false);
  const isFetchingNextPageRef = React.useRef(false);
  const fetchNextPageRef = React.useRef<() => void>(() => {});

  const initialOrderBy = queryParams.get('orderBy') || defaultOrderBy || '';
  const initialOrderByDesc =
    queryParams.get('orderByDesc') === 'true'
      ? true
      : queryParams.get('orderByDesc') === 'false'
        ? false
        : defaultOrderByDesc;

  const [searchTerm, setSearchTerm] = React.useState(
    queryParams.get('searchTerm') || ''
  );
  const [orderBy, setOrderBy] = React.useState<string>(initialOrderBy);
  const [orderByDesc, setOrderByDesc] = React.useState(initialOrderByDesc);

  const hasOverrideData = Boolean(overrideData && overrideData.length > 0);
  const infiniteEnabled = Boolean(queryFn) && !hasOverrideData;

  React.useEffect(() => {
    if (
      defaultOrderBy &&
      !queryParams.get('orderBy') &&
      !queryParams.get('orderByDesc')
    ) {
      setOrderBy(defaultOrderBy);
      setOrderByDesc(defaultOrderByDesc);
    }
  }, []);

  const serializeFilters = (filters: object | undefined) => {
    if (!filters) return '';
    return Object.entries(filters)
      .map(
        ([key, value]) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(value as string)}`
      )
      .join('&');
  };

  const updateQueryParams = (params: any) => {
    const newQueryParams = new URLSearchParams(location.search);
    let hasChange = false;

    // Page/pageSize are unused with infinite scroll; clear any stale values.
    if (newQueryParams.has('page')) {
      newQueryParams.delete('page');
      hasChange = true;
    }
    if (newQueryParams.has('pageSize')) {
      newQueryParams.delete('pageSize');
      hasChange = true;
    }

    const setOrClearParam = (
      key: string,
      value: string | number | boolean | undefined | null,
      shouldSet: boolean
    ) => {
      if (shouldSet) {
        const nextValue = value!.toString();
        if (newQueryParams.get(key) !== nextValue) {
          newQueryParams.set(key, nextValue);
          hasChange = true;
        }
      } else if (newQueryParams.has(key)) {
        newQueryParams.delete(key);
        hasChange = true;
      }
    };

    Object.keys(params).forEach(key => {
      if (key === 'filters') {
        const serializedFilters = serializeFilters(params[key]);
        if (serializedFilters) {
          if (newQueryParams.get(key) !== serializedFilters) {
            newQueryParams.set(key, serializedFilters);
            hasChange = true;
          }
        } else if (newQueryParams.has(key)) {
          newQueryParams.delete(key);
          hasChange = true;
        }
      } else if (key === 'orderBy') {
        setOrClearParam(
          key,
          params[key],
          Boolean(params[key] && params[key] !== defaultOrderBy)
        );
      } else if (key === 'orderByDesc') {
        setOrClearParam(
          key,
          params[key],
          params[key] !== defaultOrderByDesc
        );
      } else if (params[key]) {
        if (newQueryParams.get(key) !== params[key].toString()) {
          newQueryParams.set(key, params[key]);
          hasChange = true;
        }
      } else if (newQueryParams.has(key)) {
        newQueryParams.delete(key);
        hasChange = true;
      }
    });

    if (hasChange) {
      navigate({ search: newQueryParams.toString() }, { replace: true });
    }
  };

  React.useEffect(() => {
    const newQueryParams = new URLSearchParams(location.search);
    const newSearchTerm = newQueryParams.get('searchTerm') || '';
    const newOrderBy = newQueryParams.get('orderBy') || defaultOrderBy || '';
    const newOrderByDesc =
      newQueryParams.get('orderByDesc') === 'true'
        ? true
        : newQueryParams.get('orderByDesc') === 'false'
          ? false
          : defaultOrderByDesc;

    if (
      newSearchTerm !== searchTerm ||
      newOrderBy !== orderBy ||
      newOrderByDesc !== orderByDesc
    ) {
      setSearchTerm(newSearchTerm);
      setOrderBy(newOrderBy);
      setOrderByDesc(newOrderByDesc);
    }
  }, [location.search, defaultOrderBy, defaultOrderByDesc]);

  React.useEffect(() => {
    updateQueryParams({
      searchTerm,
      orderBy,
      orderByDesc,
      filters
    });
  }, [searchTerm, orderBy, orderByDesc, filters]);

  const {
    data,
    isLoading,
    isFetched,
    isError,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage
  } = useInfiniteQuery({
    queryKey: [
      ...(queryKey ?? []),
      pageSize,
      searchTerm,
      orderBy,
      orderByDesc,
      filters
    ],
    queryFn: ({ pageParam }) =>
      queryFn!(pageParam, pageSize, searchTerm, orderBy, orderByDesc, filters),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      if (Array.isArray(lastPage)) return undefined;
      const pageCount = getPageCount(lastPage);
      const currentPage = getCurrentPage(lastPage, allPages.length);
      return currentPage < pageCount ? currentPage + 1 : undefined;
    },
    enabled: infiniteEnabled && enabled,
    staleTime: 60_000,
    refetchOnWindowFocus: false
  });

  const debouncedSearch = React.useMemo(
    () =>
      debounce((value: string) => {
        setSearchTerm(value);
      }, 1000),
    []
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSearch(e.target.value);
  };

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  };

  React.useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const rows = React.useMemo(() => {
    if (hasOverrideData) {
      return overrideData;
    }

    if (!data?.pages?.length) {
      return [];
    }

    return data.pages.flatMap(extractRows);
  }, [data, hasOverrideData, overrideData]);

  const onRowsChangeRef = React.useRef(onRowsChange);
  onRowsChangeRef.current = onRowsChange;

  React.useEffect(() => {
    onRowsChangeRef.current?.(rows);
  }, [rows]);

  hasNextPageRef.current = Boolean(hasNextPage);
  isFetchingNextPageRef.current = isFetchingNextPage;
  fetchNextPageRef.current = () => {
    void fetchNextPage();
  };

  const requestNextPage = React.useCallback(() => {
    if (!hasNextPageRef.current || isFetchingNextPageRef.current) return;
    fetchNextPageRef.current();
  }, []);

  React.useEffect(() => {
    if (!infiniteEnabled || !hasNextPage || isFetchingNextPage || isLoading) {
      return;
    }

    const sentinel = loadMoreRef.current;
    if (!sentinel) return;

    // Only fetch when the sentinel enters the viewport after leaving it.
    // A short first page must not chain the rest of the list automatically.
    let canLoadOnEnter = false;
    const observer = new IntersectionObserver(
      entries => {
        const visible = entries.some(entry => entry.isIntersecting);
        if (!visible) {
          canLoadOnEnter = true;
          return;
        }
        if (!canLoadOnEnter) return;
        canLoadOnEnter = false;
        requestNextPage();
      },
      { root: null, rootMargin: '0px', threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [
    infiniteEnabled,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    rows.length,
    requestNextPage
  ]);

  const table = useReactTable({
    data: rows,
    columns,
    state: {
      sorting: orderBy
        ? [{ id: orderBy, desc: orderByDesc }]
        : defaultOrderBy
          ? [{ id: defaultOrderBy, desc: defaultOrderByDesc }]
          : []
    },
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true
  });

  const showInitialLoading =
    infiniteEnabled && (isLoading || !enabled);
  // Prefer isFetched + data so empty results still count after the first
  // successful response (avoids briefly rendering a bare header row).
  const isEmpty =
    !showInitialLoading &&
    !isError &&
    columns.length > 0 &&
    rows.length === 0 &&
    (hasOverrideData || (isFetched && Boolean(data)));
  const emptyStateContent =
    isEmpty && renderEmptyState
      ? renderEmptyState({ searchTerm })
      : null;

  React.useEffect(() => {
    onEmptyStateChange?.({ isEmpty, searchTerm });
  }, [isEmpty, searchTerm, onEmptyStateChange]);

  return (
    <div>
      {showActions && (
        <Row
          lg="auto"
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: '2rem',
            marginBottom: '2rem',
            width: '100%',
            justifyContent: 'space-between',
            marginLeft: '0'
          }}
        >
          <Form
            style={{ width: '25%', paddingLeft: '0' }}
            onSubmit={handleFormSubmit}
          >
            <Form.Control
              name="search"
              type="text"
              placeholder="Search..."
              onChange={handleSearch}
              defaultValue={searchTerm}
            />
          </Form>
          {rightSideActions}
        </Row>
      )}

      {showInitialLoading ? (
        <TablePlaceholder />
      ) : (
        <>
          {isError || columns.length === 0 ? (
            <p className="text-center">No data available.</p>
          ) : isEmpty && emptyStateContent ? (
            emptyStateContent
          ) : (
            <Table
              className="phoenix-table mb-0 p-5 border-top border-translucent table table-lg"
              hover
              bsPrefix={tableStyleOverride}
            >
              <thead>
                <tr>
                  {table.getFlatHeaders().map((header, index) => {
                    if (!header || !header.id) return null;
                    return (
                      <th
                        key={index}
                        onClick={() => {
                          if (!header.column.getCanSort()) return;
                          const columnId = header.id;
                          if (!columnId) return;
                          if (orderBy === columnId) {
                            setOrderByDesc(previous => !previous);
                          } else {
                            setOrderBy(columnId);
                            setOrderByDesc(true);
                          }
                        }}
                        id={header.id}
                        style={{
                          cursor: header.column.getCanSort()
                            ? 'pointer'
                            : 'default'
                        }}
                      >
                        {typeof header.column.columnDef.header === 'function'
                          ? header.column.columnDef.header(header.getContext())
                          : header.column.columnDef.header}
                        {orderBy === header.id && (
                          <FontAwesomeIcon
                            icon={orderByDesc ? faArrowDown : faArrowUp}
                            className="ms-2"
                          />
                        )}
                        {!orderBy && defaultOrderBy === header.id && (
                          <FontAwesomeIcon
                            icon={defaultOrderByDesc ? faArrowDown : faArrowUp}
                            className="ms-2"
                          />
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {table.getRowModel().rows.map(row => {
                  const rowData = row.original;
                  const rowStyle = getRowStyle ? getRowStyle(rowData) : {};

                  return (
                    <tr key={row.id} style={rowStyle}>
                      {row.getVisibleCells().map(cell => (
                        <td
                          key={cell.id}
                          {...cell.column.columnDef.meta?.cellProps}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}

          {isEmpty && !emptyStateContent && (
            <p className="text-center text-body-tertiary mt-4 mb-0">
              {searchTerm.trim()
                ? 'No products found for this search.'
                : 'No products found.'}
            </p>
          )}

          {infiniteEnabled && !isError && columns.length > 0 && !isEmpty && (
            <div
              ref={loadMoreRef}
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '2.5rem',
                marginTop: '1rem'
              }}
            >
              {isFetchingNextPage ? (
                <Spinner animation="border" size="sm" role="status">
                  <span className="visually-hidden">Loading more...</span>
                </Spinner>
              ) : (
                hasNextPage && (
                  <Button
                    variant="link"
                    className="p-0"
                    onClick={requestNextPage}
                  >
                    Load more
                  </Button>
                )
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DataTable;
